import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { EligibilityRule, Question } from "../types/interfaces";
import { GenericProfilingState } from "./genericProfilingSlice";
import { getAllEligibilityMessagesForQuestion } from "./utils/profilingUtils";

export const selectProfilingState = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling;
export const selectCurrentStep = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.currentStep;
export const selectAnswers = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.answers;
export const selectExternalAnswers = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.externalAnswers;
export const selectCurrentStepInfos = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.currentStepInfos;
export const selectProfilingConfig = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.profilingConfig;
export const selectIsCompleted = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.isCompleted;
export const selectIsLoading = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.loading;
export const selectVisibleSteps = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.visibleSteps;
export const selectEligibilityResult = (state: { genericProfiling: GenericProfilingState }) => state.genericProfiling.eligibilityResult;

const NO_RULES: EligibilityRule[] = [];
export const selectEligibilityRules = (state: { genericProfiling: GenericProfilingState }) =>
  state.genericProfiling.profilingConfig.eligibilityRules ?? NO_RULES;

export const selectCurrentQuestionGroup = (state: { genericProfiling: GenericProfilingState }) => {
  const { currentStep, visibleSteps } = state.genericProfiling;
  return visibleSteps.find(group => group.step === currentStep);
};

export const selectIsLastStep = (state: { genericProfiling: GenericProfilingState }) => {
  const { currentStep, visibleSteps } = state.genericProfiling;
  const currentIndex = visibleSteps.findIndex(s => s.step === currentStep);
  return currentIndex === visibleSteps.length - 1;
};

export const selectIsFirstStep = (state: { genericProfiling: GenericProfilingState }) => {
  const { currentStep, visibleSteps } = state.genericProfiling;
  const currentIndex = visibleSteps.findIndex(s => s.step === currentStep);
  return currentIndex === 0;
};

export const selectCurrentStepProgress = (state: { genericProfiling: GenericProfilingState }) => {
  const { currentStep, visibleSteps } = state.genericProfiling;
  const currentIndex = visibleSteps.findIndex(s => s.step === currentStep);
  const totalSteps = visibleSteps.length;

  return {
    current: currentIndex + 1,
    total: totalSteps,
    percentage: totalSteps > 0 ? Math.round(((currentIndex + 1) / totalSteps) * 100) : 0,
  };
};

export const selectCurrentQuestionAnswered = (state: { genericProfiling: GenericProfilingState }) => {
  const { answers } = state.genericProfiling;
  const currentGroup = selectCurrentQuestionGroup(state);

  if (!currentGroup) return false;

  return currentGroup.questions.every(q => answers[q.key] !== undefined);
};

export const selectQuestionsAndAnswersMapping = (state: { genericProfiling: GenericProfilingState }) => {
  const questionsGroups = state.genericProfiling.profilingConfig.questionsGroups;

  const questions: Record<string, string> = {};
  const answers: Record<string, string> = {};
  const questionsByGroup: Array<{ title: string; questions: Array<{ key: string; label: string }> }> = [];

  questionsGroups.forEach(group => {
    // Créer le mapping global des questions et réponses
    group.questions.forEach(question => {
      questions[question.key] = question.label;
      question.possibleAnswers.forEach(answer => {
        if (typeof answer === 'boolean' || typeof answer === 'number' || typeof answer === 'string') {
          answers[String(answer)] = String(answer);
        } else if ('key' in answer && 'label' in answer) {
          answers[String(answer.key)] = answer.label;
        }
      });
    });

    // Créer le regroupement par groupe si title existe
    if (group.title !== undefined) {
      questionsByGroup.push({
        title: group.title,
        questions: group.questions.map(question => ({
          key: question.key,
          label: question.label
        }))
      });
    }
  });

  return { questions, answers, questionsByGroup };
};

export const selectAllQuestions = createSelector(
  [(state: RootState) => state.genericProfiling.profilingConfig.questionsGroups],
  (questionsGroups) => {
    const allQuestions: Question[] = [];

    questionsGroups.forEach(group => {
      group.questions.forEach(question => {
        allQuestions.push(question);
      });
    });

    return allQuestions;
  }
);

export const selectQuestionByKey = (questionKey: string) => (state: { genericProfiling: GenericProfilingState }) => {
  const questionsGroups = state.genericProfiling.profilingConfig.questionsGroups;

  for (const group of questionsGroups) {
    const question = group.questions.find(q => q.key === questionKey);
    if (question) return question;
  }
  return undefined;
};

export const selectQuestionsAndAnswersMappingWithEligibility = (
  state: { genericProfiling: GenericProfilingState }
) => {
  const questionsGroups = state.genericProfiling.profilingConfig.questionsGroups;
  const eligibilityRules = state.genericProfiling.profilingConfig.eligibilityRules || [];

  const questions: Record<string, string> = {};
  const answers: Record<string, string> = {};
  const eligibilityMessages: Record<string, Record<string, string>> = {}; // questionKey -> answerKey -> message
  const questionsByGroup: Array<{
    title: string;
    questions: Array<{
      key: string;
      label: string;
      eligibilityMessages: Record<string, string>;
    }>
  }> = [];

  questionsGroups.forEach(group => {
    const groupQuestions: Array<{
      key: string;
      label: string;
      eligibilityMessages: Record<string, string>;
    }> = [];

    group.questions.forEach(question => {
      // Mapping global des questions
      questions[question.key] = question.label;

      // Mapping global des réponses
      question.possibleAnswers.forEach(answer => {
        if (typeof answer === 'boolean' || typeof answer === 'number' || typeof answer === 'string') {
          answers[String(answer)] = String(answer);
        } else if ('key' in answer && 'label' in answer) {
          answers[String(answer.key)] = answer.label;
        }
      });

      // Obtenir les messages d'éligibilité pour cette question
      const questionEligibilityMessages = getAllEligibilityMessagesForQuestion(
        question.key,
        question.possibleAnswers,
        eligibilityRules
      );

      eligibilityMessages[question.key] = questionEligibilityMessages;

      groupQuestions.push({
        key: question.key,
        label: question.label,
        eligibilityMessages: questionEligibilityMessages
      });
    });

    if (group.title !== undefined) {
      questionsByGroup.push({
        title: group.title,
        questions: groupQuestions
      });
    }
  });

  return {
    questions,
    answers,
    questionsByGroup,
    eligibilityMessages
  };
};
